import { useQuery } from '@tanstack/react-query';
import { Layers } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { groupsOf } from '@/api/auth';
import { questionSetsQuery, type QuestionSet } from '@/api/questionSets';
import { useSession } from '@/app/session';
import { ApiErrorState } from '@/components/states/ApiErrorState';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Table, type Column } from '@/components/ui/Table';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { ListPager } from './ListPager';
import {
  questionSetUrl,
  readSetsFilters,
  setsSearch,
  SETS_PAGE_SIZE,
  toQuestionSetListQuery,
} from './questionBankUrls';
import { SetDialog } from './SetDialog';
import { setAuthorLabel, type Viewer } from './setAccess';
import { SetMenu } from './SetMenu';

/**
 * `DESIGN.md` §6.9 — **the tenant's question sets**, at `/questions`.
 *
 * ⚠️ **One list, and the *Questions* tab is deleted rather than hidden** (2026-09-24, ADR 0019 — *a
 * set owns its questions*). §6.9 was two tabs at one address from 2026-08-12: *Sets*, and *Questions*
 * — the tenant-wide library Sprint 19 built, and both nouns are **retracted** here. The user read that screen and answered it — *"the Questions
 * tab looks redundant, it has all the questions and I do not know what it is for; there should be only
 * question sets, and adding, removing and so on should be done inside each set"*, and, asked what
 * should happen to a question several sets hold, *"each set of questions can be its own, even if the
 * questions repeat"* (translated; ADR 0019 quotes both in the original). Those two sentences delete
 * the library **screen** and the library **entity** together,
 * because a question shared between sets is exactly what a library is. Adding, editing, removing,
 * restoring and ordering a question all happen inside a set now (`SetScreen`).
 *
 * ⚠️ **What went with the tab, so that none of it reads as mislaid**: the tab bar and `?tab=`, the
 * three client-side filters and their address parameters, the library's rows and its view helpers
 * (`BankFilters`, `BankRows`, `bankView`), the *pick from the library* picker (`AddToSetDialog`), the
 * whole of `api/questions.ts`, `queryKeys.questions` and its root — and **the one-press-one-history-
 * entry guard**, whose subject was Radix's tab trigger and which is deleted with it rather than kept
 * as a precaution against a component this screen no longer renders (`questionBankUrls.ts` records the
 * finding, which is not retracted). Nothing under `src/` imports any of them.
 *
 * ⚠️ **The rows are narrowed per reader and the route is refused to nobody** — two different things,
 * both true, and the client renders neither (ADR 0018's 2026-08-12 reach entry, restated by ADR 0019
 * rather than re-decided). All three groups call `GET /api/v1/question-sets/` and are answered `200`;
 * an `Admin` or a `Recruiter` gets the tenant's sets, and anybody else gets the sets they authored plus
 * the sets attached to a vacancy their own assignments touch. **There is therefore no group gate on
 * this screen and none at its route** — a gate over all three groups is a gate that can never refuse,
 * which is `/candidates`' own argument — and the narrowing is the server's, because the migration
 * named every set after its vacancy and a flat list would hand an `Interviewer` the catalogue
 * `GET /api/v1/roles/` answers them **403**. That is why the cache key carries the **viewer**: this
 * route is narrowed rather than refused, which is exactly the distinction `roleOptions` has and `roles`
 * has not. ⚠️ **Since ADR 0019 that one predicate governs questions too** — a question is reachable
 * only through its set.
 *
 * ⚠️ **The author is a name, and it is uniform for every reader** (ADR 0016 decision 5(a)). The same
 * string is rendered to an `Admin`, a `Recruiter` and an `Interviewer` — never resolved per viewer,
 * which is the shape BUG-057 refused for §6.5's card — and it is never an email address: the server
 * resolves it through `name_without_an_address` precisely because `display_name` falls back to
 * `username`, which **is** the work address of every account `POST /api/v1/users/` creates. A `null`
 * means **nobody wrote this set** (`setAuthorLabel`), which is a different state from an account with
 * no name, and it is the row an `Admin` alone may edit. ⚠️ **This is now the *only* catalogue list this
 * product answers, and 5(a) holds more easily for it**: it enumerates artefacts rather than accounts,
 * carries one fact about the person, and is not a search surface over people.
 *
 * ⚠️ **`?include_retired=true`, always, and the row says which it is in words** (§3): *Restore* has to
 * be able to find its target, and a retired set that vanished the moment it was retired would be a
 * state with no way back into it — the defect TASK-114 shipped on the staff surface and TASK-115 had
 * to come back for. Nothing here dims a row by opacity; retired values take `ink-500`, which is a text
 * colour that clears §9's floor, and the controls in the last cell are never dimmed at all.
 *
 * ⚠️ **A retired row never leaves the table, so nothing here moves focus.** That is why this screen
 * needs none of the focus machinery `SetItems` carries: the live-pass finding it was built for
 * (`document.activeElement` on `<body>` after a write took the row off screen) cannot happen where the
 * row stays and `Dialog` hands focus back to the `⋯` it was opened from. The **announcement** is owed
 * either way and is here.
 */
export default function QuestionBank() {
  const session = useSession();

  /**
   * No session, no viewer — and the viewer decides which rows carry a `⋯`, so the honest answer while
   * it resolves is §8's loading state, which is what this would render anyway. `RequireSession` is
   * upstream, so this is a frame rather than a state anyone sits in.
   */
  if (!session.data) return <LoadingSets />;

  return <SetsScreen viewer={{ id: session.data.user.id, groups: groupsOf(session.data) }} />;
}

function SetsScreen({ viewer }: { viewer: Viewer }) {
  const { t } = useTranslation('questions');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * The address bar is the page (`questionBankUrls.ts`), so a page survives a reload, the back button
   * undoes a page change, and the screen has one place to read what is applied from.
   *
   * ⚠️ **A parameter this reader does not know is not read and is not rewritten.** A bookmark of
   * `/questions?tab=questions&category=TECHNICAL&page=3` lands on page 3 of the sets list — the tab and
   * the category are simply not read, which is §6.3's *"dropped rather than sent"* rule applied to a
   * **name** rather than to a value, and it is why a stale link degrades instead of erroring.
   */
  const filters = readSetsFilters(searchParams);

  /**
   * Whether *New set* has been pressed. Mounted **once**, by this component and only while open, so
   * both places the button appears (the header and §8's empty state) open the same form.
   */
  const [creating, setCreating] = useState(false);

  /**
   * What the live region currently says. Component state and not server state: it records an
   * interaction this component performed, nothing reads it back as data, and it survives the refetch
   * the write asks for because this component stays mounted through it.
   */
  const [announcement, setAnnouncement] = useState('');

  const sets = useQuery(questionSetsQuery(viewer.id, toQuestionSetListQuery(filters)));

  if (sets.error !== null) {
    /**
     * One mapping for every failure (`ApiErrorState`): offline, forbidden, throttled, or this screen's
     * own sentence. The `h1` stays — a failed screen with no heading at all is the defect Sprint 8
     * found in a live browser — and so does the lead, because §8's states are not dead ends.
     */
    return (
      <Frame>
        <div className="mt-10">
          <ApiErrorState
            error={sets.error}
            message={t('sets.error')}
            onRetry={() => void sets.refetch()}
          />
        </div>
      </Frame>
    );
  }

  /** §8's loading state: rows shaped like rows (`Table` owns the shape), never a centred spinner. */
  if (sets.data === undefined) return <LoadingSets />;

  const rows = sets.data.items;
  const count = sets.data.count;

  const retiredInk = (row: QuestionSet) => (row.is_active ? undefined : 'text-ink-500');

  const columns: Array<Column<QuestionSet>> = [
    {
      key: 'name',
      header: t('sets.table.columns.name'),
      primary: true,
      /*
       * ⚠️ **A real `<a href>` in the primary cell, and it is the one link on this screen.** §4's
       * `Table` is given a callback rather than a URL and its own note says the anchor belongs in the
       * cell that has one — so the address is built by `questionSetUrl` and never spelled here, which
       * is what the href walk in this screen's suite resolves against `buildRoutes()`.
       */
      cell: (row) => (
        <Link
          to={questionSetUrl(row.id)}
          className={cn('underline-offset-2 hover:underline', retiredInk(row))}
        >
          {row.name}
        </Link>
      ),
    },
    {
      key: 'questions',
      header: t('sets.table.columns.questions'),
      /*
       * ⚠️ **The figure carries its own words**, which is §6.8's rule and was paid for there: *"a bare
       * pair of numerals in a cell is two figures nobody can name"*. It is the **active** count — the
       * server defines it once, on the schema, so §6.9's label and the number cannot drift — and it is
       * the count of what a new interview would freeze, not of how many rows the set holds.
       */
      cell: (row) => (
        <span className={retiredInk(row)}>
          {t('sets.table.activeCount', { count: row.active_question_count })}
        </span>
      ),
    },
    {
      key: 'author',
      header: t('sets.table.columns.author'),
      cell: (row) => (
        <span className={retiredInk(row)}>{setAuthorLabel(row, t('sets.table.noAuthor'))}</span>
      ),
    },
    {
      key: 'state',
      header: t('sets.table.columns.state'),
      // The state carries its text, as §3 and §9 require: a row marked by colour alone is invisible
      // to a screen reader and to anyone with no active row beside it to compare against.
      // `whitespace-nowrap` for the measured reason §6.9 records — the cell's own break rule would
      // otherwise split a one-word state down to a letter's width.
      cell: (row) => (
        <span className={cn(type.eyebrow, 'whitespace-nowrap')}>
          {row.is_active ? t('sets.state.active') : t('sets.state.retired')}
        </span>
      ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('sets.table.columns.actions')}</span>,
      align: 'right',
      // Empty on a row this viewer may not write, rather than carrying a disabled control (§5). The
      // column stays whatever any given row offers: one that appeared and vanished with the viewer's
      // own rows would be a layout moving for a reason nobody can see.
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          <SetMenu
            set={row}
            viewer={viewer}
            onStateChanged={(changed, nowActive) =>
              setAnnouncement(
                t(nowActive ? 'sets.restore.announced' : 'sets.retire.announced', {
                  name: changed.name,
                }),
              )
            }
          />
        </div>
      ),
    },
  ];

  return (
    <Frame>
      <div className="mt-6 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <p className={cn(type.metadata, type.numeral)}>{t('sets.counts.total', { count })}</p>
        {/*
         * ⚠️ **One *New set* control on screen at a time**, which is §6.8's rule and TASK-072 paid for
         * it: here when the screen has rows, and as §8's *"No question sets yet."* action when it has
         * none. It is deliberately **absent** on a `?page=` past the end — offering the create action
         * to somebody whose bookmarked address merely overshot is how a second set for the same job
         * gets made — and it carries **no group check**, because all three groups may create a set of
         * their own (ADR 0016 decision 5(c)) and a predicate here could never be false.
         */}
        {rows.length > 0 && <Button onClick={() => setCreating(true)}>{t('sets.newSet')}</Button>}
      </div>

      {/*
       * Always mounted, `sr-only`, and empty until something happens: a live region added to the page
       * together with its first message is not reliably announced, and one that is already there is.
       */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>

      <div className="mt-4">
        <Table
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          empty={
            /*
             * ⚠️ **§8's two empty states, and they are told apart by the address rather than guessed
             * at.** An empty page 2 is somebody's bookmark overshooting and offers **no** create
             * action — §6.3's `?page=99` failure, which §6.8 and §6.9 have both paid for — while an
             * empty page 1 is a workspace with no sets, and the one action that helps is the one that
             * makes the first.
             */
            filters.page > 1 ? (
              <EmptyState icon={Layers} message={t('sets.empty.page')} />
            ) : (
              <EmptyState
                icon={Layers}
                message={t('sets.empty.none')}
                action={<Button onClick={() => setCreating(true)}>{t('sets.newSet')}</Button>}
              />
            )
          }
        />
      </div>

      {/* The pager is on screen whenever the list runs to more than one page, and on an overshot page
          too — that is the control the reader needs to get back. */}
      {(count > SETS_PAGE_SIZE || filters.page > 1) && (
        <ListPager
          page={filters.page}
          shown={rows.length}
          count={count}
          pageSize={SETS_PAGE_SIZE}
          onGoToPage={(page) => setSearchParams(setsSearch({ page }))}
        />
      )}

      {creating && (
        <SetDialog
          onClose={() => setCreating(false)}
          /*
           * ⚠️ **A new set is empty, so the only useful next step is composing it** — and the create
           * answers with the set itself (`QuestionSetDetailOut`), so the destination is known without
           * a second request. This is the one place in the family that navigates on a write, and it is
           * the reason `SetDialog` hands the created row back instead of only closing.
           */
          onCreated={(created) => void navigate(questionSetUrl(created.id))}
        />
      )}
    </Frame>
  );
}

/**
 * The screen's heading and its one sentence, above whatever state the list is in — §9's single `h1`,
 * on screen in every one of §8's states, which is the rule Sprint 8 found broken in a live browser (an
 * error state with no heading at all).
 *
 * ⚠️ **The sentence was widened for two lists on 2026-08-12 (TASK-142) and is narrowed again here**
 * (2026-09-24), and the wording it read is **retracted** rather than merely replaced. It was *"Every
 * question in this workspace, whoever wrote it, and the sets an interview is built from…"*, written
 * when a question could be listed on its own; with one list it is
 * half false in the other direction — there is no tenant-wide list of questions to describe, and a set
 * **owns** the questions in it rather than selecting them from somewhere. What the new sentence has to
 * carry is the model the user asked for: a set is what an interview is built from, its questions are
 * its own, and who may change one is what the row menus say by being there or not.
 */
function Frame({ children }: { children?: React.ReactNode }) {
  const { t } = useTranslation('questions');

  return (
    <div>
      <h1 className={type.pageTitle}>{t('title')}</h1>
      <p className={cn(type.body, 'mt-1')}>{t('lead')}</p>
      {children}
    </div>
  );
}

/** §8's loading state: rows shaped like rows (`Table` owns the shape), never a centred spinner. */
function LoadingSets() {
  return (
    <Frame>
      <div className="mt-6">
        <Table columns={[]} rows={[]} rowKey={() => 0} isLoading />
      </div>
    </Frame>
  );
}
